/**
 * Hub submit UI — Framer presence, timeline, Done CTA, pulse/shimmer CSS.
 *
 * Belongs here: shared chrome for interactive + submitted visits.
 * Does not belong here: POST / retries (see useHubSubmit); page visit latch.
 */
import type { ReactNode } from 'react'
import { useLayoutEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { PrimaryButton } from '../../../../components/execution/PrimaryButton'
import { getBottomPadding } from '../../../../utils/sessionStorage'
import { EventNames } from '../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../utils/appBridge'
import { ExpertReviewEventIds, ExpertReviewScreen } from '../analytics/expertReviewEvents'
import { CountdownSegment } from '../../plans-v4/lumpsum/plans-list/timer/shared/PlanCountdownSegments'
import { usePlanCountdown } from '../../plans-v4/lumpsum/plans-list/timer/shared/usePlanCountdown'
import { HubSubmitPhase } from './useHubSubmit'

export { HubSubmitPhase } from './useHubSubmit'

/** Shared spring for card center + HubPresence height collapse. */
export const HUB_SPRING = { type: 'spring' as const, duration: 0.5, bounce: 0 }

/** Spinner → timeline grow — longer ease-out so the card eases up without a snap. */
const HUB_TIMELINE_EXPAND = { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const }

const HUB_SUBMIT_SPINNER_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
  <circle cx="16" cy="16" r="8.25" stroke="#D1D6D6" stroke-width="1.5"/>
  <path d="M16 7.75C20.5564 7.75 24.25 11.4436 24.25 16" stroke="#232323" stroke-width="1.5" stroke-linecap="round"/>
</svg>`

const HUB_REVIEW_SUBMITTED_ICON = `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M20 33C27.7321 33 34 26.7321 34 19C34 11.268 27.7321 5 20 5C12.268 5 6 11.268 6 19C6 26.7321 12.268 33 20 33Z" fill="#232323"/>
  <path d="M15 20.6667L18.3951 24L26 14" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const HUB_REVIEW_CURRENT_ICON = `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M20 5.75C27.3179 5.75 33.25 11.6822 33.25 19C33.25 26.3179 27.3179 32.25 20 32.25C12.6822 32.25 6.75 26.3179 6.75 19C6.75 11.6822 12.6822 5.75 20 5.75Z" fill="white" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M20 22C21.6569 22 23 20.6569 23 19C23 17.3431 21.6569 16 20 16C18.3431 16 17 17.3431 17 19C17 20.6569 18.3431 22 20 22Z" fill="#232323"/>
</svg>`

/**
 * Show/hide with height spring so collapse stays in sync with the card centering.
 * Used for title, step list, footer, and status row on the interactive visit.
 * Reduced motion → plain mount/unmount (no spring).
 */
export function HubPresence({
  show,
  className = '',
  children
}: {
  show: boolean
  className?: string
  children: ReactNode
}) {
  const prefersReducedMotion = useReducedMotion()

  if (prefersReducedMotion === true) {
    if (!show) return null
    return <div className={className}>{children}</div>
  }

  return (
    <AnimatePresence initial={false}>
      {show ? (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={HUB_SPRING}
          className={`overflow-hidden ${className}`}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

/**
 * Row under the card: spinner while submitting, review timeline when submitted.
 * Hidden while pending. Interactive visit only (submitted visit renders HubReviewTimeline directly).
 *
 * Submitting → submitted: height eases from spinner → timeline; second timeline
 * pointer drops with the same ease so it stays in sync with the card rising.
 */
export function HubSubmitStatusRow({
  phase,
  firstPlanDropTime
}: {
  phase: HubSubmitPhase
  firstPlanDropTime?: number | null
}) {
  const prefersReducedMotion = useReducedMotion()
  const isSending = phase === HubSubmitPhase.Submitting
  const contentRef = useRef<HTMLDivElement>(null)
  const previousHeightRef = useRef(0)
  const [height, setHeight] = useState<number | 'auto'>('auto')

  useLayoutEffect(() => {
    const node = contentRef.current
    if (!node) return

    const nextHeight = node.getBoundingClientRect().height
    const previousHeight = previousHeightRef.current

    if (
      prefersReducedMotion !== true &&
      previousHeight > 0 &&
      Math.abs(previousHeight - nextHeight) > 1
    ) {
      // Paint clipped frame, then ease open (double rAF so the from-height sticks)
      setHeight(previousHeight)
      let raf2 = 0
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setHeight(nextHeight))
      })
      previousHeightRef.current = nextHeight
      return () => {
        cancelAnimationFrame(raf1)
        cancelAnimationFrame(raf2)
      }
    }

    previousHeightRef.current = nextHeight
    setHeight('auto')
  }, [isSending, firstPlanDropTime, prefersReducedMotion])

  return (
    <HubPresence show={phase !== HubSubmitPhase.Pending}>
      <div className="pt-8 pb-20">
        <motion.div
          initial={false}
          animate={{ height }}
          transition={prefersReducedMotion === true ? { duration: 0 } : HUB_TIMELINE_EXPAND}
          className="overflow-hidden"
          onAnimationComplete={() => setHeight('auto')}
        >
          <div ref={contentRef}>
            {isSending ? (
              <div className="flex items-center gap-3 px-1">
                <span
                  className="hub-spinner flex h-8 w-8 shrink-0 items-center justify-center"
                  aria-hidden
                >
                  <RenderHTML
                    innerHTMLText={HUB_SUBMIT_SPINNER_ICON}
                    className="[&>svg]:block h-8 w-8"
                  />
                </span>
                <p className="min-w-0 flex-1 text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
                  Submitting portfolio…
                </p>
              </div>
            ) : (
              <HubReviewTimeline animateEnter firstPlanDropTime={firstPlanDropTime} />
            )}
          </div>
        </motion.div>
      </div>
    </HubPresence>
  )
}

/**
 * Two-step review timeline — day/hour/minute countdown when epoch known; fallback copy otherwise.
 *
 * @param animateEnter - live submit only: second pointer drops from the first with
 *   HUB_TIMELINE_EXPAND (same ease as status-row height / card rise). Cold restore skips.
 */
export function HubReviewTimeline({
  firstPlanDropTime,
  animateEnter = false
}: {
  firstPlanDropTime?: number | null
  animateEnter?: boolean
}) {
  const prefersReducedMotion = useReducedMotion()
  const countdownParts = usePlanCountdown(firstPlanDropTime)
  const hasDropTime = typeof firstPlanDropTime === 'number'
  const skipEnter = !animateEnter || prefersReducedMotion === true
  const enterTransition = skipEnter ? { duration: 0 } : HUB_TIMELINE_EXPAND
  /** Second step drop distance — connector counters this y so it draws from the first icon. */
  const dropY = 36
  const connectorHeight = 38

  return (
    <ol>
      <motion.li
        initial={skipEnter ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={enterTransition}
        className="flex items-center gap-4"
      >
        <span className="shrink-0" aria-hidden>
          <RenderHTML innerHTMLText={HUB_REVIEW_SUBMITTED_ICON} className="[&>svg]:block" />
        </span>
        <p className="min-w-0 flex-1 text-[16px] font-[500] leading-[120%] text-[#232323]">
          Portfolio submitted
        </p>
      </motion.li>
      <motion.li
        initial={skipEnter ? false : { y: -dropY, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={enterTransition}
        className="relative mt-7 flex items-start gap-4"
        aria-current="step"
      >
        {/* Inverse y keeps line top under the first icon while height grows as this row drops */}
        <motion.span
          initial={skipEnter ? false : { height: 0, y: dropY }}
          animate={{ height: connectorHeight, y: 0 }}
          transition={enterTransition}
          className="pointer-events-none absolute left-5 top-[-34px] w-[1.5px] -ml-[0.75px] bg-[#F0F4F7]"
          aria-hidden
        />
        <span className="shrink-0" aria-hidden>
          <RenderHTML innerHTMLText={HUB_REVIEW_CURRENT_ICON} className="[&>svg]:block" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col pt-[8px]">
          <p className="text-[16px] font-[500] leading-[120%] text-[#232323]">
            Portfolio under review
          </p>
          <p className="text-[13px] pt-1 font-[400] leading-[140%] text-[#6A6E6E]">
            Our experts are reviewing your portfolio and preparing your plans.{' '}
            {hasDropTime ? 'Your plan will be ready in:' : 'Ready in a few days'}
          </p>
          {hasDropTime ? (
            <HubReviewCountdown
              days={countdownParts.days}
              hours={countdownParts.hours}
              minutes={countdownParts.minutes}
            />
          ) : null}
        </div>
      </motion.li>
    </ol>
  )
}

/** Grey offset shadow + digit color from the hub done design. Days, hours, minutes only. */
function HubReviewCountdown({
  days,
  hours,
  minutes
}: {
  days: number
  hours: number
  minutes: number
}) {
  return (
    // pr/pb keep the 2px offset shadow inside overflow-hidden parents (submit height + hub page)
    <div className="mt-4 pr-0.5 pb-0.5">
      <div
        className="grid grid-cols-3 items-stretch overflow-hidden rounded-[20px] border border-[#D1D6D6] bg-white shadow-[2px_2px_0_0_#D1D6D6]"
        role="group"
        aria-label={`Ready in ${days} days, ${hours} hours, and ${minutes} minutes`}
      >
        <CountdownSegment
          value={days}
          label="days"
          showDividerAfter
          digitClassName="text-[#3D3D3D]"
          unitLabelClassName="text-[#6A6E6E]"
        />
        {/* Outer padding only — pulls hours and minutes toward each other */}
        <div className="col-span-2 flex min-w-0 px-2">
          <CountdownSegment
            value={hours}
            label="hours"
            digitClassName="text-[#3D3D3D]"
            unitLabelClassName="text-[#6A6E6E]"
          />
          <CountdownSegment
            value={minutes}
            label="minutes"
            digitClassName="text-[#3D3D3D]"
            unitLabelClassName="text-[#6A6E6E]"
          />
        </div>
      </div>
    </div>
  )
}

/**
 * Done CTA — pinned at bottom.
 * Interactive: animateEnter when phase becomes submitted.
 * Submitted visit: show with animateEnter false (resting frame, no enter motion).
 */
export function HubDoneFooter({
  show,
  onDone,
  animateEnter = true
}: {
  show: boolean
  onDone: () => void
  animateEnter?: boolean
}) {
  const prefersReducedMotion = useReducedMotion()
  const skipMotion = !animateEnter || prefersReducedMotion === true

  const button = (
    <PrimaryButton
      testLocator="allocation-plan-done"
      label="Done"
      onClick={() => {
        lightImpactVibrate()
        trackEvent({
          eventName: EventNames.ButtonTap,
          payload: {
            ScreenName: ExpertReviewScreen.Hub,
            eventId: ExpertReviewEventIds.HubDone,
            buttonLabel: 'Done'
          }
        })
        onDone()
      }}
      primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px]"
    />
  )

  if (skipMotion) {
    if (!show) return null
    return (
      <div
        className="absolute inset-x-5 z-20"
        style={{ bottom: `calc(${getBottomPadding()}px + 16px)` }}
      >
        {button}
      </div>
    )
  }

  return (
    <AnimatePresence>
      {show ? (
        <motion.div
          key="hub-done-footer"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={HUB_SPRING}
          className="absolute inset-x-5 z-20"
          style={{ bottom: `calc(${getBottomPadding()}px + 16px)` }}
        >
          {button}
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

/**
 * Injects keyframes for card pulse / shimmer / spinner.
 * Mount once on HubInteractiveScreen; classes are toggled from HubCard via shouldPlayCardSubmitPulse.
 */
export function HubSubmitStyles() {
  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `
          /* Applied only while shouldPlayCardSubmitPulse — opacity breathe, not scale (scale fought layout). */
          @keyframes hub-card-scale-breathe {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.94; }
          }
          @keyframes hub-card-bg-pulse {
            0%, 100% { opacity: 0.55; }
            50% { opacity: 0.9; }
          }
          @keyframes hub-card-breathe {
            0%, 100% { opacity: 0; }
            50% { opacity: 0.1; }
          }
          @keyframes hub-card-shimmer {
            0% { transform: translateX(-130%); }
            100% { transform: translateX(220%); }
          }
          @keyframes hub-spin {
            to { transform: rotate(360deg); }
          }
          .hub-card--pulse {
            animation: hub-card-scale-breathe 1s ease-in-out infinite;
          }
          .hub-card-bg--sending {
            animation: hub-card-bg-pulse 1s ease-in-out infinite;
          }
          .hub-card-breathe {
            pointer-events: none;
            position: absolute;
            inset: 0;
            z-index: 1;
            background: #ffffff;
            opacity: 0;
            animation: hub-card-breathe 1s ease-in-out infinite;
          }
          .hub-card-shimmer {
            pointer-events: none;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            z-index: 2;
            /* Narrow band; peak white ~22% so it does not read as chrome shine. */
            width: 42%;
            background: linear-gradient(
              105deg,
              transparent 0%,
              rgba(255, 255, 255, 0.08) 38%,
              rgba(255, 255, 255, 0.22) 50%,
              rgba(255, 255, 255, 0.08) 62%,
              transparent 100%
            );
            animation: hub-card-shimmer 0.9s ease-in-out infinite;
          }
          .hub-spinner {
            animation: hub-spin 1s linear infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .hub-card--pulse,
            .hub-card-bg--sending,
            .hub-card-breathe,
            .hub-card-shimmer,
            .hub-spinner {
              animation: none;
            }
          }
        `
      }}
    />
  )
}
